import { Bot, BotOff, Zap } from 'lucide-react'

import { Button } from '@/common/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

import { useMutationUpdateChatAiMode } from '../../../api/hooks'
import type { AiAnswerMode } from '../../../api/services'

type ChatAiModeSelectorProps = {
	roomId: string
	currentMode: AiAnswerMode
}

const MODE_META: Record<
	AiAnswerMode,
	{ label: string; description: string; iconClass: string }
> = {
	ASSIS: {
		label: 'ASSIS',
		description: 'ช่วยร่าง — ไม่ส่งอัตโนมัติ',
		iconClass: 'text-primary',
	},
	AUTO: {
		label: 'AUTO',
		description: 'ตอบอัตโนมัติ — บันทึกร่างแล้วส่ง 1 ข้อความให้ผู้เล่น',
		iconClass: 'text-emerald-500',
	},
	OFF: {
		label: 'OFF',
		description: 'ปิดการทำงานของ AI ในห้องนี้',
		iconClass: 'text-muted-foreground',
	},
}

function ModeIcon({
	mode,
	className,
}: {
	mode: AiAnswerMode
	className?: string
}) {
	if (mode === 'AUTO') return <Zap className={className} />
	if (mode === 'OFF') return <BotOff className={className} />
	return <Bot className={className} />
}

export function ChatAiModeSelector({
	roomId,
	currentMode,
}: ChatAiModeSelectorProps) {
	const mutation = useMutationUpdateChatAiMode()
	const currentMeta = MODE_META[currentMode]

	const handleSelect = (mode: AiAnswerMode) => {
		if (mode === currentMode) return
		mutation.mutate({ roomId, aiAnswerMode: mode })
	}

	return (
		<DropdownMenu>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								disabled={mutation.isPending}
								aria-label={`โหมด AI: ${currentMeta.label}`}
							>
								<ModeIcon
									mode={currentMode}
									className={`h-4 w-4 ${currentMeta.iconClass}`}
								/>
							</Button>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent>
						โหมด AI: {currentMeta.label} — {currentMeta.description}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<DropdownMenuContent align="end" className="w-64">
				<DropdownMenuLabel>โหมดตอบกลับ AI</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{(['ASSIS', 'AUTO', 'OFF'] as AiAnswerMode[]).map((mode) => {
					const meta = MODE_META[mode]
					const isActive = mode === currentMode
					return (
						<DropdownMenuItem
							key={mode}
							onClick={() => handleSelect(mode)}
							disabled={mutation.isPending}
							className="flex items-start gap-2 py-2"
						>
							<ModeIcon
								mode={mode}
								className={`mt-0.5 h-4 w-4 shrink-0 ${meta.iconClass}`}
							/>
							<div className="flex-1 space-y-0.5">
								<div className="flex items-center gap-2">
									<span className="text-sm font-medium">{meta.label}</span>
									{isActive && (
										<span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
											ใช้งานอยู่
										</span>
									)}
								</div>
								<p className="text-xs text-muted-foreground">
									{meta.description}
								</p>
							</div>
						</DropdownMenuItem>
					)
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
